import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle';
import XCircle from 'lucide-react/dist/esm/icons/x-circle';
import AlertCircle from 'lucide-react/dist/esm/icons/alert-circle';
import Info from 'lucide-react/dist/esm/icons/info';
import X from 'lucide-react/dist/esm/icons/x';
import type { LucideIcon } from 'lucide-react';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastConfig {
  icon: LucideIcon;
  bgClass: string;
  iconClass: string;
  progressClass: string;
}

interface ToastData {
  id: number;
  type: ToastType;
  title?: string;
  message: string;
  duration: number;
}

export interface ToastMethods {
  success: (message: string, title?: string) => number;
  error: (message: string, title?: string) => number;
  warning: (message: string, title?: string) => number;
  info: (message: string, title?: string) => number;
  loading: (message: string, title?: string) => number;
}

// Toast Context
const ToastContext = createContext<ToastMethods | null>(null);

// Toast types with icons and colors
const toastConfig: Record<ToastType, ToastConfig> = {
  success: {
    icon: CheckCircle,
    bgClass: 'bg-status-success/15 border-status-success/30',
    iconClass: 'text-status-success',
    progressClass: 'bg-status-success',
  },
  error: {
    icon: XCircle,
    bgClass: 'bg-red-500/15 border-red-500/30',
    iconClass: 'text-red-400',
    progressClass: 'bg-red-400',
  },
  warning: {
    icon: AlertCircle,
    bgClass: 'bg-amber-500/15 border-amber-500/30',
    iconClass: 'text-amber-400',
    progressClass: 'bg-amber-400',
  },
  info: {
    icon: Info,
    bgClass: 'bg-brand/15 border-brand/30',
    iconClass: 'text-primary',
    progressClass: 'bg-brand',
  },
};

// Individual Toast Component
interface ToastItemProps {
  id: number;
  type?: ToastType;
  title?: string;
  message: string;
  duration?: number;
  onClose: (id: number) => void;
}

const Toast: React.FC<ToastItemProps> = ({ id, type = 'info', title, message, duration = 4000, onClose }) => {
  const [isLeaving, setIsLeaving] = useState(false);
  const [progress, setProgress] = useState(100);
  const config = toastConfig[type];
  const Icon = config.icon;

  const handleClose = useCallback(() => {
    setIsLeaving(true);
    setTimeout(() => onClose(id), 300);
  }, [id, onClose]);

  useEffect(() => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      
      if (remaining <= 0) {
        clearInterval(interval);
        handleClose();
      }
    }, 50);

    return () => clearInterval(interval);
  }, [duration, handleClose]);

  return (
    <div
      className={`relative flex items-start gap-3 min-w-[320px] max-w-[420px] p-4 rounded-xl border backdrop-blur-xl shadow-2xl transition-all duration-300 ${config.bgClass} ${
        isLeaving ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0'
      }`}
      style={{ animation: 'slideInRight 0.3s ease-out' }}
    >
      {/* Icon */}
      <div className={`flex-shrink-0 ${config.iconClass}`}>
        <Icon size={20} />
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        {title && (
          <p className="text-content font-semibold text-sm mb-0.5">{title}</p>
        )}
        <p className="text-content-secondary text-sm m-0">{message}</p>
      </div>
      
      {/* Close Button */}
      <button
        onClick={handleClose}
        className="flex-shrink-0 p-1 rounded-lg text-content-muted hover:text-content hover:bg-surface-elevated transition-colors"
      >
        <X size={16} />
      </button>
      
      {/* Progress Bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-surface-elevated rounded-b-xl overflow-hidden">
        <div
          className={`h-full ${config.progressClass} transition-all duration-100 ease-linear`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

// Toast Container
interface ToastContainerProps {
  toasts: ToastData[];
  removeToast: (id: number) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, removeToast }) => {
  return createPortal(
    <div className="fixed top-4 right-4 z-toast flex flex-col gap-3">
      {toasts.map((toast) => (
        <Toast key={toast.id} {...toast} onClose={removeToast} />
      ))}
    </div>,
    document.body,
  );
};

// Toast Provider
interface ToastProviderProps {
  children: React.ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const addToast = useCallback(({ type = 'info' as ToastType, title, message, duration = 4000 }: { type?: ToastType; title?: string; message: string; duration?: number }) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, type, title, message, duration }]);
    return id;
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const toast: ToastMethods = {
    success: (message: string, title?: string) => addToast({ type: 'success', title, message }),
    error: (message: string, title?: string) => addToast({ type: 'error', title, message }),
    warning: (message: string, title?: string) => addToast({ type: 'warning', title, message }),
    info: (message: string, title?: string) => addToast({ type: 'info', title, message }),
    loading: (message: string, title?: string) => addToast({ type: 'info', title, message }),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

// Hook to use toast
export const useToast = (): ToastMethods => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default Toast;
